import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import { post } from '../../Auths/AuthToken';
import {
  addDays,
  compareAsc,
  endOfDay,
  endOfMonth,
  endOfWeek,
  isBefore,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { setAppPopupFacility } from '../../../redux/slices/scheduler/appointmentSlice';
import { formatTime } from '../../../Utilities/Utilities';

function AppCalander({
  providerId = 0,
  appDate,
  originalAppDate,
  callback = () => {},
  facilityList = [],
  facilityLoc,
  mode = 0,
  setMode = () => {},
}) {
  const [allData, setAllData] = useState([]);
  const [allBlockData, setAllBlockData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [facilityLocCal, setFacilityLocCal] = useState(0);

  const APPOINTMENT_STATUS = {
    NO_SHOW: '1',
    CANCELED: '2',
    RESCHEDULED: '3',
  };

  const [facilitySetting, setFacilitySetting] = useState(null);
  const [calTime, setCalTime] = useState({
    startTime: null,
    endTime: null,
    scrliiTime: null,
  });

  const dispatch = useDispatch();
  const { user } = useSelector((store) => store.auth);
  const { defaultTimeFormat } = useSelector((store) => store.app);
  const { patSrchDiasbled, globalAppId, bookNewApp } = useSelector((store) => store.appointment);

  const isExistingApp = globalAppId && !bookNewApp?.status;
  const isOriginalPast = originalAppDate && isBefore(
    new Date(originalAppDate).setHours(0, 0, 0, 0),
    new Date().setHours(0, 0, 0, 0),
  );
  const isCalendarBlocked = isExistingApp && isOriginalPast;

  const [calPeriodFilter, setCalPeriodFilter] = useState({
    name: 'day',
    selectDate: appDate,
    startDate: appDate,
    endDate: appDate,
  });

  const parseTime = useCallback(
    (timeStr, refDate = calPeriodFilter.startDate) => {
      if (!timeStr || timeStr === 'NULL') return null;
      let parsed = null;

      // Handle full timestamp strings (e.g., "2026-04-12 06:00:00")
      // We extract strictly the TIME portion to ensure it remains relative to the day being viewed
      if (
        timeStr.includes(' ') &&
        timeStr.includes('-') &&
        timeStr.includes(':')
      ) {
        const timePart = timeStr.split(' ')[1];
        parsed = parse(timePart, 'HH:mm:ss', refDate);
      } else if (timeStr.includes(':')) {
        // Handle time strings with AM/PM or military formats
        if (timeStr.includes('AM') || timeStr.includes('PM')) {
          parsed = parse(timeStr, 'h:mm aa', refDate);
        } else {
          const parts = timeStr.split(':');
          if (parts.length === 3) {
            parsed = parse(timeStr, 'HH:mm:ss', refDate);
          } else if (parts.length === 2) {
            parsed = parse(timeStr, 'HH:mm', refDate);
          }
        }
      } else {
        parsed = new Date(timeStr);
      }

      const isValidDate = parsed && !isNaN(parsed.getTime());
      return isValidDate ? parsed : null;
    },
    [calPeriodFilter.startDate],
  );

  const weekStartsOnSetting = useMemo(() => {
    if (facilitySetting?.facility_start_day === '1') return 1;
    if (facilitySetting?.facility_start_day === '0') return 0;
    return 1; // Default to Monday as per project constants
  }, [facilitySetting]);

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () =>
      startOfWeek(new Date(), {
        weekStartsOn: weekStartsOnSetting,
      }),
    getDay,
    locales: {
      'en-US': require('date-fns/locale/en-US'),
    },
  });

  useEffect(() => {
    if (
      appDate &&
      calPeriodFilter.selectDate?.getTime() !== appDate.getTime()
    ) {
      setCalPeriodFilter((prev) => ({
        ...prev,
        selectDate: appDate,
        startDate:
          prev.name === 'day'
            ? appDate
            : prev.name === 'week'
              ? startOfWeek(appDate, { weekStartsOn: weekStartsOnSetting })
              : startOfMonth(appDate),
        endDate:
          prev.name === 'day'
            ? appDate
            : prev.name === 'week'
              ? endOfWeek(appDate, { weekStartsOn: weekStartsOnSetting })
              : endOfMonth(appDate),
      }));
    }
  }, [appDate, weekStartsOnSetting]);

  useEffect(() => {
    let activeRequest = true;

    const fetchData = async () => {
      if (!providerId) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await post('api/auth/appCalander', {
          resourceId: providerId,
          locationId: mode === 0 ? facilityLoc : facilityLocCal,
          start: format(calPeriodFilter.startDate, 'yyyy-MM-dd'),
          end: format(calPeriodFilter.endDate, 'yyyy-MM-dd'),
          staffUserId: user?.id,
          mode: mode,
        });

        if (activeRequest) {
          if (response.status === 200) {
            setAllData((prev) => {
              const isAllLocationMode =
                mode === 1 && parseInt(facilityLocCal) === 0;
              return {
                ...response.data,
                defaultLocation: isAllLocationMode
                  ? prev?.defaultLocation
                  : response.data.defaultLocation,
                defaultLocationName: isAllLocationMode
                  ? prev?.defaultLocationName
                  : response.data.defaultLocationName,
              };
            });
            setAllBlockData(response?.data?.eventsClose || []);
            setFacilitySetting(response?.data?.facilitySetting || {});
            const overrides = Array.isArray(response?.data?.overrideSetting)
              ? response.data.overrideSetting
              : [response?.data?.overrideSetting].filter(Boolean);

            const activeOverride = overrides.find((o) => {
              const isWithinRange =
                o?.start_date &&
                o?.end_date &&
                startOfDay(calPeriodFilter.startDate) >=
                  startOfDay(new Date(o.start_date)) &&
                startOfDay(calPeriodFilter.startDate) <=
                  startOfDay(new Date(o.end_date));
              const dayId =
                calPeriodFilter.startDate.getDay() === 0
                  ? 7
                  : calPeriodFilter.startDate.getDay();
              return parseInt(o?.day_id) === dayId && isWithinRange;
            });

            const isOverride = activeOverride?.schedule_type === '1';

            const finalCalTime = {
              startTime: parseTime(
                response?.data?.facilitySetting?.facility_first_hour,
              ),
              endTime: parseTime(
                response?.data?.facilitySetting?.facility_last_hour,
              ),
              scrliiTime: parseTime(
                response?.data?.facilitySetting?.facility_scroll_hour,
              ),
            };
            setCalTime(finalCalTime);
          } else {
            setError('Failed to fetch calendar data.');
          }
        }
      } catch (err) {
        if (activeRequest) {
          console.error('Error fetching calendar data:', err);
          setError('An error occurred while loading the calendar.');
        }
      } finally {
        if (activeRequest) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      activeRequest = false;
    };
  }, [providerId, facilityLocCal, calPeriodFilter, user, facilityLoc, mode]);

  const onRangeChange = useCallback(
    (range, view) => {
      if (view && calPeriodFilter?.name !== view) {
        if (range.length === 1 && view === 'day') {
          setCalPeriodFilter({
            ...calPeriodFilter,
            name: 'day',
            startDate: calPeriodFilter.selectDate,
            endDate: calPeriodFilter.selectDate,
          });
        } else if (range.length === 7) {
          setCalPeriodFilter({
            ...calPeriodFilter,
            name: 'week',
            startDate: startOfWeek(calPeriodFilter.selectDate, {
              weekStartsOn: weekStartsOnSetting,
            }),
            endDate: endOfWeek(calPeriodFilter.selectDate, {
              weekStartsOn: weekStartsOnSetting,
            }),
          });
        } else {
          setCalPeriodFilter({
            ...calPeriodFilter,
            name: 'month',
            startDate: startOfMonth(calPeriodFilter.selectDate),
            endDate: endOfMonth(calPeriodFilter.selectDate),
          });
        }
      }
    },
    [calPeriodFilter, facilitySetting?.facility_start_day],
  );

  const onNavigate = (newDate, view, action) => {
    if (view === 'day') {
      setCalPeriodFilter({
        ...calPeriodFilter,
        name: 'day',
        selectDate: newDate,
        startDate: newDate,
        endDate: newDate,
      });
      callback(new Date(newDate));
    } else if (view === 'week') {
      setCalPeriodFilter({
        ...calPeriodFilter,
        name: 'week',
        selectDate: newDate,
        startDate: startOfWeek(newDate, {
          weekStartsOn: weekStartsOnSetting,
        }),
        endDate: endOfWeek(newDate, {
          weekStartsOn: weekStartsOnSetting,
        }),
      });
    } else if (view === 'month') {
      setCalPeriodFilter({
        ...calPeriodFilter,
        name: 'month',
        selectDate: newDate,
        startDate: startOfMonth(newDate),
        endDate: endOfMonth(newDate),
      });
    }
  };

  const eventPropGetter = useCallback((event) => {
    let style = {
      color: 'black',
      backgroundColor: 'blue',
      pointerEvents: 'auto',
    };

    if (event.isVirtual) {
      if (event.type === 'break') {
        style = {
          backgroundColor: 'powderblue',
          color: 'black',
          pointerEvents: 'none',
          border: '1px solid #bdd1d1',
          fontSize: '11px',
          padding: '2px',
        };
      } else if (event.type === 'block') {
        style = {
          backgroundColor: '#7a7e7c',
          color: 'white',
          pointerEvents: 'none',
          fontSize: '11px',
          padding: '2px',
        };
      }
    } else {
      if (event.app_status === APPOINTMENT_STATUS.NO_SHOW) {
        style.backgroundColor = 'gray';
      } else if (event.app_status === APPOINTMENT_STATUS.CANCELED) {
        style.backgroundColor = 'gray';
      } else if (event.app_status === APPOINTMENT_STATUS.RESCHEDULED) {
        style.backgroundColor = 'gray';
      } else {
        if (event.app_color) {
          style.backgroundColor = event.app_color;
        }
      }
    }

    return { style };
  }, []);

  const handleSelectSlot = useCallback(
    (event) => {
      if (calPeriodFilter?.name === 'month' || event.start.getHours() >= 7) {
        callback(new Date(event?.start));
      }
    },
    [callback, calPeriodFilter],
  );

  const onSelectEvent = useCallback(
    (event) => {
      if (event.isVirtual) return; // Ignore clicks on breaks/blocks
      callback(new Date(event?.start));
    },
    [callback],
  );

  const getActiveSchedule = useCallback(
    (date) => {
      const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay();
      const dateMs    = startOfDay(date).getTime();

      const overrides = Array.isArray(allData?.overrideSetting)
        ? allData.overrideSetting
        : [allData?.overrideSetting].filter(Boolean);

      const isActive = (o, type) => {
        if (!o?.start_date || !o?.end_date || o?.schedule_type !== type) return false;
        const s = startOfDay(parseISO(o.start_date)).getTime();
        const e = startOfDay(parseISO(o.end_date)).getTime();
        if (isNaN(s) || isNaN(e)) return false;
        return parseInt(o.day_id) === dayOfWeek && dateMs >= s && dateMs <= e;
      };

      const tempOverride = overrides.find((o) => isActive(o, '2'));
      if (tempOverride) return { schedule: tempOverride, isAfterEnd: false };

      const weeklyOverride = overrides.find((o) => isActive(o, '1'));
      if (weeklyOverride) return { schedule: weeklyOverride, isAfterEnd: false };

      const regularList = Array.isArray(allData?.providerScheduleEnd)
        ? allData.providerScheduleEnd
        : allData?.providerScheduleEnd ? [allData?.providerScheduleEnd] : [];

      const regular = regularList.find((s) => parseInt(s.day_id) === dayOfWeek);
      if (regular) {
        const endStr     = regular.end_date;
        const hasEnd     = endStr && endStr !== 'NULL' && endStr !== '0000-00-00' && endStr !== '';
        const isAfterEnd = hasEnd && startOfDay(date) > startOfDay(new Date(endStr));
        return { schedule: regular, isAfterEnd };
      }

      return { schedule: null, isAfterEnd: false };
    },
    [allData?.overrideSetting, allData?.providerScheduleEnd],
  );

  const getOverrideForDate = useCallback(
    (date) => getActiveSchedule(date).schedule,
    [getActiveSchedule],
  );

  const combinedEvents = useMemo(() => {
    if (!allData) return [];

    if (calPeriodFilter.name === 'month') {
      return allData?.events || [];
    }

    const virtualEvents = [];
    const viewStart = startOfDay(new Date(calPeriodFilter.startDate));
    const viewEnd = endOfDay(new Date(calPeriodFilter.endDate));

    let cur = new Date(viewStart);
    while (cur <= viewEnd) {
      const currentCur = cur;
      const dayOfWeek = currentCur.getDay() === 0 ? 7 : currentCur.getDay();
      const overrides = Array.isArray(allData?.overrideSetting)
        ? allData.overrideSetting
        : [allData?.overrideSetting].filter(Boolean);

      const type1Override = overrides.find((o) => {
        if (!o?.start_date || !o?.end_date || o?.schedule_type !== '1')
          return false;
        const d = startOfDay(currentCur).getTime();
        const s = startOfDay(parseISO(o.start_date)).getTime();
        const e = startOfDay(parseISO(o.end_date)).getTime();
        if (isNaN(s) || isNaN(e)) return false;
        return parseInt(o?.day_id) === dayOfWeek && d >= s && d <= e;
      });

      const providerRegularSetting = Array.isArray(allData?.providerScheduleEnd)
        ? allData.providerScheduleEnd.find(
            (s) => parseInt(s.day_id) === dayOfWeek,
          )
        : parseInt(allData?.providerScheduleEnd?.day_id) === dayOfWeek
          ? allData?.providerScheduleEnd
          : null;

      const providerEndDateStr = providerRegularSetting?.end_date;
      const isAfterScheduleEnd =
        providerEndDateStr &&
        providerEndDateStr !== 'NULL' &&
        providerEndDateStr !== '0000-00-00' &&
        providerEndDateStr !== '' &&
        startOfDay(currentCur) > startOfDay(new Date(providerEndDateStr));

      const activeBase = type1Override || (!isAfterScheduleEnd ? providerRegularSetting : null);

      let break1 = { start: null, end: null, title: 'Break' };
      let break2 = { start: null, end: null, title: 'Break' };

      if (activeBase && activeBase.is_closed === '1') {
        break1 = {
          start: activeBase.break_first_start_time,
          end: activeBase.break_first_end_time,
          title: activeBase.break_first_name || 'Break',
        };
        break2 = {
          start: activeBase.break_second_start_time,
          end: activeBase.break_second_end_time,
          title: activeBase.break_second_name || 'Break',
        };
      }

      const dailyBreaks = [break1, break2];

      dailyBreaks.forEach((b) => {
        if (b.start && b.end) {
          const s = parseTime(b.start, currentCur);
          const e = parseTime(b.end, currentCur);
          if (s && e && compareAsc(s, e) < 0) {
            virtualEvents.push({
              start: s,
              end: e,
              title: b.title,
              isVirtual: true,
              type: 'break',
            });
          }
        }
      });
      cur = addDays(cur, 1);
    }

    allBlockData?.forEach((item) => {
      if (item.is_task === '2' && item.start && item.end) {
        virtualEvents.push({
          start: parseISO(item.start),
          end: parseISO(item.end),
          title: item.title || 'Blocked',
          isVirtual: true,
          type: 'block',
        });
      }
    });

    return [...(allData?.events || []), ...virtualEvents];
  }, [
    allData,
    allBlockData,
    calPeriodFilter.startDate,
    calPeriodFilter.endDate,
    parseTime,
  ]);

  const GRAY_DAY = {
    className: 'blocked-day',
    style: { pointerEvents: 'none', backgroundColor: '#7a7e7c' },
  };

  const dayPropGetter = useCallback(
    (date) => {
      const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay();
      const dateStr   = format(date, 'yyyy-MM-dd');

      const hasOpenTask = allData?.openMyCalander?.some((interval) => {
        const start = parseISO(interval.start_time);
        return format(start, 'yyyy-MM-dd') === dateStr;
      });
      if (hasOpenTask) return {};

      const { schedule, isAfterEnd } = getActiveSchedule(date);

      if (!schedule) {
        const facilityDay = allData?.facilityHourSetting?.find(
          (f) => parseInt(f.day_id) === dayOfWeek,
        );
        if (!facilityDay || facilityDay.is_closed === '0') return GRAY_DAY;
        return {};
      }

      if (isAfterEnd) return GRAY_DAY;

      if (schedule.is_closed === '0') return GRAY_DAY;

      return {};
    },
    [allData, getActiveSchedule],
  );

  const { formats } = useMemo(() => {
    return {
      formats: {
        eventTimeRangeFormat: ({ start, end }, culture, localizer) => {},
      },
    };
  }, []);

  const isSlotOutsideHours = useCallback((slotDate, workOpen, workClose) => {
    if (!workOpen && !workClose) return false;
    const h = slotDate.getHours();
    const m = slotDate.getMinutes();
    if (workOpen  && (h < workOpen.getHours()  || (h === workOpen.getHours()  && m < workOpen.getMinutes())))  return true;
    if (workClose && (h > workClose.getHours() || (h === workClose.getHours() && m >= workClose.getMinutes()))) return true;
    return false;
  }, []);

  const GRAY_SLOT = { style: { backgroundColor: 'gray', color: 'white' } };

  const slotPropGetter = useCallback(
    (date) => {
      if (calPeriodFilter.name === 'month') return {};

      const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay();

      const isInOpenTask = allData?.openMyCalander?.some((interval) => {
        const start = parseISO(interval.start_time);
        const end   = parseISO(interval.end_time);
        return compareAsc(start, end) < 0 && isWithinInterval(date, { start, end });
      });
      if (isInOpenTask) return { style: { backgroundColor: '#fff', color: 'black' } };

      const isInBlockedTask = allBlockData?.some(
        (item) =>
          item.is_task === '2' && item.start && item.end &&
          isWithinInterval(date, { start: parseISO(item.start), end: parseISO(item.end) }),
      );
      if (isInBlockedTask) return { style: { backgroundColor: '#7a7e7c', color: 'white' } };

      const { schedule, isAfterEnd } = getActiveSchedule(date);

      const facilityDay = allData?.facilityHourSetting?.find(
        (f) => parseInt(f.day_id) === dayOfWeek,
      );

      if (!schedule) {
        if (!facilityDay || facilityDay.is_closed === '0') return GRAY_SLOT;
        const facOpen  = parseTime(allData?.facilitySetting?.facility_first_hour, date);
        const facClose = parseTime(allData?.facilitySetting?.facility_last_hour, date);
        return isSlotOutsideHours(date, facOpen, facClose) ? GRAY_SLOT : {};
      }

      if (isAfterEnd) return GRAY_SLOT;

      if (schedule.is_closed === '0') return GRAY_SLOT;

      const isFullDayBlocked = allBlockData?.some(
        (item) => parseInt(item.day_id) === dayOfWeek && (!item.start || item.title === 'Full day Block'),
      );
      if (isFullDayBlocked) return GRAY_SLOT;

      const workOpen  = parseTime(schedule.open_time  || allData?.startHour || allData?.facilitySetting?.facility_first_hour, date);
      const workClose = parseTime(schedule.close_time || allData?.lastHour  || allData?.facilitySetting?.facility_last_hour,  date);
      if (isSlotOutsideHours(date, workOpen, workClose)) return GRAY_SLOT;

      return {};
    },
    [allData, allBlockData, getActiveSchedule, calPeriodFilter.name, parseTime, isSlotOutsideHours],
  );

  const handleCheckboxChange = (event) => {
    //const { name, value, checked } = event.target;
    //dispatch(setAppPopupFacility(parseInt(event.target.value)));
    setFacilityLocCal(parseInt(event.target.value));
    setMode(1);
  };
  useEffect(() => {
    console.log('facilityLocCal', facilityLocCal, mode);
  }, [facilityLocCal, mode]);

  const CustomToolbar = (toolbar) => {
    return (
      <div
        className="rbc-toolbar"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          fontSize: '14px',
        }}
      >
        <div style={{ display: 'flex', width: '100%', margin: '10px' }}>
          {(calPeriodFilter.name === 'day' && allData?.displayDate && (
            <span className="rbc-toolbar-labels">{`${allData?.dayName}, ${allData?.displayDate}:  ${
              allData?.providerOfficeTime?.[0]?.toLowerCase()?.trim() ===
              'closed'
                ? getOverrideForDate(calPeriodFilter.selectDate) ||
                  allData?.openMyCalander?.some((interval) => {
                    const start = parseISO(interval.start_time);
                    return (
                      format(start, 'yyyy-MM-dd') ===
                        format(calPeriodFilter.selectDate, 'yyyy-MM-dd') &&
                      compareAsc(
                        parseISO(interval.start_time),
                        parseISO(interval.end_time),
                      ) < 0
                    );
                  })
                  ? '00:00'
                  : 'Closed'
                : `${formatTime(allData?.providerOfficeTime?.[0], defaultTimeFormat)} to ${formatTime(allData?.providerOfficeTime?.[1], defaultTimeFormat)}`
            }`}</span>
          )) ||
            (calPeriodFilter.name === 'week' && (
              <span className="rbc-toolbar-labels">{`Week: ${format(
                calPeriodFilter?.startDate,
                'MMMM d, yyyy',
              )} to ${format(calPeriodFilter?.endDate, 'MMMM d, yyyy')}`}</span>
            )) ||
            (calPeriodFilter.name === 'month' && (
              <span className="rbc-toolbar-labels">{`Month: ${format(
                calPeriodFilter?.endDate,
                'MMMM, yyyy',
              )}`}</span>
            ))}
        </div>
        <div
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <span className="rbc-btn-group">
            <button
              type="button"
              onClick={
                isCalendarBlocked
                  ? () => {}
                  : () => toolbar.onNavigate('PREV')
              }
            >
              Prev
            </button>
            <button
              type="button"
              onClick={
                isCalendarBlocked
                  ? () => {}
                  : () => toolbar.onNavigate('TODAY')
              }
            >
              Today
            </button>
            <button
              type="button"
              onClick={
                isCalendarBlocked
                  ? () => {}
                  : () => toolbar.onNavigate('NEXT')
              }
            >
              Next
            </button>
          </span>
          {/* {() => {
          if (calPeriodFilter.name === 'day') {
            return <span className="rbc-toolbar-label">{toolbar.label}</span>;
          }
        }} */}
          {/* <span className="rbc-toolbar-label">{toolbar.label}</span> */}
          <span className="rbc-btn-group">
            <button
              className={calPeriodFilter.name === 'day' ? 'rbc-active' : ''}
              type="button"
              onClick={
                isCalendarBlocked
                  ? () => {}
                  : () => toolbar.onView('day')
              }
            >
              Day
            </button>
            <button
              className={calPeriodFilter.name === 'week' ? 'rbc-active' : ''}
              type="button"
              onClick={
                isCalendarBlocked
                  ? () => {}
                  : () => toolbar.onView('week')
              }
            >
              Week
            </button>
            <button
              className={calPeriodFilter.name === 'month' ? 'rbc-active' : ''}
              type="button"
              onClick={
                isCalendarBlocked
                  ? () => {}
                  : () => toolbar.onView('month')
              }
            >
              Month
            </button>
          </span>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className=" width100 apt-sldr-loc-sec">
        {error && (
          <div
            style={{
              padding: '10px',
              margin: '10px',
              backgroundColor: '#fee2e2',
              color: '#b91c1c',
              borderRadius: '4px',
              fontSize: '14px',
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex' }}>
          <div className="sldr-loctn-slectn" style={{ width: '30%' }}>
            <span className="">
              <input
                type="radio"
                name={'facilityLocCal'}
                checked={
                  parseInt(facilityLocCal) ===
                  parseInt(allData?.defaultLocation)
                }
                onChange={handleCheckboxChange}
                value={
                  allData?.defaultLocation
                    ? parseInt(allData.defaultLocation)
                    : 0
                }
              />
            </span>
            {!isLoading ? (
              <span className="" style={{ padding: '0px 10px' }}>
                {allData?.defaultLocationName}
                {`${allData?.is_telemedicine === '1' ? '(Remote)' : ''}`}
              </span>
            ) : (
              <span className="" style={{ padding: '0px 10px' }}>
                Loading...
              </span>
            )}
          </div>
          <div className="sldr-loctn-slectn" style={{ width: '30%' }}>
            <span className="">
              <input
                type="radio"
                name={'facilityLocCal'}
                checked={parseInt(facilityLocCal) === 0}
                onChange={handleCheckboxChange}
                value={0}
              />
            </span>
            <span className="" style={{ padding: '0px 10px' }}>
              All Locations
              {`${allData?.is_telemedicine === '1' ? '(Remote)' : ''}`}
            </span>
          </div>
        </div>
        <div className="left med-label width100 fontsize14">
          Total appointments: {allData?.appointmentTotalCount}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;New patients:
          {allData?.appointmentTotalCountNewPt}
        </div>
      </div>

      <Calendar
        key={allData?.events?.length + combinedEvents.length}
        localizer={localizer}
        {...(defaultTimeFormat?.withoutSecond === 'HH:mm'
          ? {
              formats: {
                timeGutterFormat: 'HH:mm', // 24hr time format
                eventTimeRangeFormat: ({ start, end }) =>
                  `${start.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false, // 24hr format
                  })} - ${end.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false, // 24hr format
                  })}`,
              },
            }
          : formats)}
        events={combinedEvents}
        startAccessor={(event) => {
          return new Date(event.start);
        }}
        endAccessor={(event) => {
          return new Date(event.end);
        }}
        titleAccessor={(event) => {
          let title = event.title;
          if (event.app_status === APPOINTMENT_STATUS.NO_SHOW) {
            title = 'No Show';
          } else if (event.app_status === APPOINTMENT_STATUS.CANCELED) {
            title = 'Canceled';
          } else if (event.app_status === APPOINTMENT_STATUS.RESCHEDULED) {
            title = 'Rescheduled';
          }
          return title;
        }}
        toolbar={true}
        selectable={true}
        views={['month', 'week', 'day']}
        defaultView={calPeriodFilter.name || 'day'}
        date={calPeriodFilter?.selectDate}
        //defaultDate={calPeriodFilter?.selectDate || new Date()}
        eventPropGetter={eventPropGetter}
        dayPropGetter={dayPropGetter}
        slotPropGetter={slotPropGetter}
        style={{ height: 500, margin: '10px 10px', width: '570px' }}
        min={calTime?.startTime ? new Date(calTime?.startTime) : undefined} // Set the minimum time
        max={calTime?.endTime ? new Date(calTime?.endTime) : undefined} // Set the maximum time
        step={
          facilitySetting?.calander_default_slot
            ? parseInt(facilitySetting.calander_default_slot)
            : 15
        }
        // formats={formats}
        onRangeChange={onRangeChange}
        onNavigate={onNavigate}
        onSelectSlot={handleSelectSlot}
        onSelectEvent={onSelectEvent}
        scrollToTime={
          calTime?.scrliiTime ? new Date(calTime?.scrliiTime) : new Date()
        }
        components={{
          toolbar: CustomToolbar,
        }}
      />
    </div>
  );
}

export default AppCalander;
